import { ActionTree, MutationTree, GetterTree } from 'vuex'
import { State, TypedActionTree, LoadingErrorState } from '@/store/types'
import CatalogService from '@/services/CatalogService'
import { Lihe, Service } from '@/services/CatalogService.interface'

// Define actions for catalog
export type CatalogActions = 'fetchLihes' | 'fetchServices'

// Vuex State for Catalog
export interface CatalogState extends LoadingErrorState<CatalogActions> {
  lihes: Partial<Lihe>[]
  services: Partial<Service>[]
}

// Initial State
const state: CatalogState = {
  lihes: [],
  services: [],
  loading: {},
  error: {},
}

// Mutations
const mutations: MutationTree<CatalogState> = {
  setLihes(state, lihes: Partial<Lihe>[]) {
    state.lihes = [...state.lihes, ...lihes]
  },
  setServices(state, services: Partial<Service>[]) {
    state.services = services
  },
  setLoading(
    state,
    { action, status }: { action: CatalogActions; status: boolean }
  ) {
    state.loading[action] = status
  },
  setError(
    state,
    { action, message }: { action: CatalogActions; message: string | null }
  ) {
    state.error[action] = message
  },
}

// Actions using TypedActionTree
const actions: TypedActionTree<CatalogActions, CatalogState, State> = {
  async fetchLihes({ commit }, liheIds: number[]) {
    const action = 'fetchLihes'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })
    try {
      const catalogService = new CatalogService()
      const lihes = await Promise.all(
        liheIds.map(liheId => catalogService.getLihe(liheId))
      )
      commit('setLihes', lihes)
    } catch (error) {
      console.error('Error fetching Lihes:', error)
      commit('setError', {
        action,
        message: 'Failed to fetch Lihe details.',
      })
    } finally {
      commit('setLoading', { action, status: false })
    }
  },

  async fetchServices({ commit }, { serviceIds }: { serviceIds: number[] }) {
    const action = 'fetchServices'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })
    try {
      const catalogService = new CatalogService()
      const services = await catalogService.getServices(serviceIds)
      commit('setServices', services)
    } catch (error) {
      console.error('Error fetching Services:', error)
      commit('setError', {
        action,
        message: 'Failed to fetch Services.',
      })
    } finally {
      commit('setLoading', { action, status: false })
    }
  },
}

// Getters
const getters: GetterTree<CatalogState, State> = {
  /**
   * Check if any action is loading
   */
  isLoadingAny: (state: CatalogState) => {
    return Object.values(state.loading).some(status => status)
  },

  /**
   * Check if a specific action is loading
   */
  isLoading: (state: CatalogState) => (action: CatalogActions) => {
    return !!state.loading[action]
  },

  /**
   * Check if there are any errors
   */
  hasErrorAny: (state: CatalogState) => {
    return Object.values(state.error).some(message => message !== null)
  },

  /**
   * Get the error message for a specific action
   */
  getError: (state: CatalogState) => (action: CatalogActions) => {
    return state.error[action] || null
  },

  /**
   * Get Lihe by ID
   */
  getLiheById:
    (state: CatalogState) =>
    (liheId: number): Partial<Lihe> | null => {
      return (
        state.lihes.find((lihe: Partial<Lihe>) => lihe.liheId === liheId) ||
        null
      )
    },

  /**
   * Get all services
   */
  getServices: (state: CatalogState) => {
    return state.services
  },

  /**
   * Get all lihes
   */
  getLihes: (state: CatalogState) => {
    return state.lihes
  },
}

// Export the Vuex module
export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
